import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CornerDownLeft, Search } from "lucide-react";
import { pages } from "../state/nav";
import { useAskOlli } from "../../hooks/useFeatureFlags";

// Pages the app has (a design page with no route yet can't be opened, so it isn't listed).
const entries = [
  ...pages.filter((p) => p.path).map((p) => ({ path: p.path, title: p.title, group: p.group?.label, soon: p.soon })),
  { path: "/wallet/transactions", title: "Transactions", group: "Wallet", soon: false },
];

/** ⌘K page search. Arrow keys move, Enter opens, Esc closes. */
export function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const askOlliOn = useAskOlli();
  const visible = askOlliOn ? entries : entries.filter((e) => e.path !== "/ask-olli");
  const [q, setQ] = useState("");
  const [index, setIndex] = useState(0);
  const rows = useRef([]);

  useEffect(() => {
    if (open) {
      setQ("");
      setIndex(0);
    }
  }, [open]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return visible;
    return visible.filter((e) => e.title.toLowerCase().includes(s) || (e.group ?? "").toLowerCase().includes(s));
  }, [q, askOlliOn]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    rows.current[index]?.scrollIntoView({ block: "nearest" });
  }, [index]);

  if (!open) return null;

  const go = (path) => {
    navigate(path);
    onClose();
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[index]) {
      go(results[index].path);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex animate-fade-in justify-center bg-ink/30 px-4 pt-[16vh]" onMouseDown={onClose}>
      <div
        role="dialog"
        aria-label="Search pages"
        onMouseDown={(e) => e.stopPropagation()}
        className="flex h-fit max-h-[64vh] w-[640px] max-w-full animate-nav-in flex-col overflow-hidden rounded-xl bg-paper shadow-lift ring-1 ring-line"
      >
        <div className="flex h-14 shrink-0 items-center gap-3 border-b border-line px-5">
          <Search size={18} strokeWidth={1.75} className="text-muted" />
          <input
            autoFocus
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setIndex(0);
            }}
            onKeyDown={onKey}
            placeholder="Search pages"
            aria-label="Search pages"
            className="h-full flex-1 bg-transparent text-body text-ink outline-none placeholder:text-muted"
          />
          <kbd className="rounded-xs bg-mist px-2 py-0.5 font-sans text-caption font-medium text-muted">Esc</kbd>
        </div>

        {results.length === 0 ? (
          <div className="flex flex-col items-center gap-1 px-6 py-10 text-center">
            <p className="text-body font-semibold">No pages match "{q.trim()}"</p>
            <p className="text-small text-muted">Try another word</p>
          </div>
        ) : (
          <ul className="flex flex-col gap-0.5 overflow-y-auto p-2" role="listbox">
            {results.map((r, i) => (
              <li key={r.path}>
                <button
                  ref={(el) => {
                    rows.current[i] = el;
                  }}
                  role="option"
                  aria-selected={i === index}
                  onMouseMove={() => setIndex(i)}
                  onClick={() => go(r.path)}
                  className={`flex h-11 w-full items-center gap-3 rounded-md px-3 text-left ${i === index ? "bg-sun-soft" : ""}`}
                >
                  <span className="flex-1 truncate text-small font-medium text-ink">{r.title}</span>
                  {r.soon && <span className="rounded-full bg-mist px-2 py-0.5 text-caption font-medium text-muted">Soon</span>}
                  {r.group && <span className="text-caption text-muted">{r.group}</span>}
                  {i === index && <CornerDownLeft size={14} strokeWidth={1.75} className="text-muted" />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
